import { AlertBanner } from '@/components/alert-banner';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  PURGE_ENROLLED_STUDENT_SAMPLE_SIZE,
  PurgeEnrolledStudent,
  PurgeEnrolledStudents,
  studentYearOptionOnlyNumber,
} from '@airreading/common/data';
import { ReactNode, useEffect, useRef, useState } from 'react';

type CheckState =
  | { status: 'running' }
  | { status: 'done'; result: PurgeEnrolledStudents }
  | { status: 'failed'; message: string };

const studentLabel = (student: PurgeEnrolledStudent) => {
  const name = [student.firstName, student.lastName].filter(Boolean).join(' ');
  const grade = studentYearOptionOnlyNumber(student.grade);
  const schools = student.studentSchools.map((school) => school.name).join(', ');

  return [name || student.studentId, grade && `grade ${grade}`, schools].filter(Boolean).join(' — ');
};

interface PurgeEnrolledStudentsGateProps {
  checkEnrolledStudents: () => Promise<PurgeEnrolledStudents>;
  onCleared?: () => void;
  children: ReactNode;
}

export const PurgeEnrolledStudentsGate = ({
  checkEnrolledStudents,
  onCleared,
  children,
}: PurgeEnrolledStudentsGateProps) => {
  const [state, setState] = useState<CheckState>({ status: 'running' });

  // Held in refs so the check runs once for the confirmed target rather than
  // re-firing whenever a callback identity changes, matching the dry run.
  const checkRef = useRef(checkEnrolledStudents);
  const onClearedRef = useRef(onCleared);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const result = await checkRef.current();
        if (cancelled) return;

        setState({ status: 'done', result });

        if (result.enrolledCount === 0) {
          onClearedRef.current?.();
        }
      } catch (e: unknown) {
        if (cancelled) return;
        const message = airServerErrorFrom(e)?.userFacingMessage || 'Could not check student statuses';
        setState({ status: 'failed', message });
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'running') {
    return (
      <p className="flex items-center gap-1 text-sm text-gray-600" data-testid="purge-enrolled-students-checking">
        <LoaderIcon className="h-3 w-3" />
        Checking student statuses…
      </p>
    );
  }

  if (state.status === 'failed') {
    return (
      <p className="text-sm text-destructive" data-testid="purge-enrolled-students-failed">
        {state.message}
      </p>
    );
  }

  const { enrolledCount, students } = state.result;

  if (enrolledCount === 0) {
    return <>{children}</>;
  }

  const unnamedCount = enrolledCount - students.length;

  return (
    <div data-testid="purge-enrolled-students-warning">
      <AlertBanner
        title={`${enrolledCount.toLocaleString()} ${enrolledCount === 1 ? 'student is' : 'students are'} still enrolled`}
        content={
          <>
            <p>
              Every student has to be dropped or marked program concluded before the data purge can run. Update the
              {enrolledCount === 1 ? ' student' : ' students'} below, then confirm again to continue.
            </p>
            <ul className="mt-2 list-disc pl-5">
              {students.map((student) => (
                <li key={student.studentId}>{studentLabel(student)}</li>
              ))}
            </ul>
            {unnamedCount > 0 && (
              <p className="mt-2">
                and {unnamedCount.toLocaleString()} more, only the first {PURGE_ENROLLED_STUDENT_SAMPLE_SIZE} are
                listed.
              </p>
            )}
          </>
        }
      />
    </div>
  );
};
